import React from 'react';
import { Box, CircleOff, LayoutList, Menu } from 'lucide-react';
import { formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент для визуализации объема бассейна
 */
const VisualizationSection = ({ 
  formData, 
  results, 
  activeTab, 
  setActiveTab 
}) => {
  if (!results) return null;

  // Определение размеров для визуализации
  const maxSize = 300; // максимальный размер для отображения в пикселях
  const aspectRatio = 1.5; // соотношение сторон для контейнера

  // Расчет размеров для визуализации бассейна в зависимости от типа
  let visWidth, visHeight, visDepth;
  
  if (formData.poolType === 'rectangular') {
    const length = parseFloat(formData.length);
    const width = parseFloat(formData.width);
    const ratio = length / width;
    
    if (ratio > 1) {
      visWidth = maxSize;
      visHeight = maxSize / ratio;
    } else {
      visHeight = maxSize;
      visWidth = maxSize * ratio;
    }
    
    visDepth = Math.min(70, (parseFloat(formData.depth) / length) * maxSize * 0.8);
  } 
  else if (formData.poolType === 'circular') {
    const diameter = parseFloat(formData.diameter);
    visWidth = visHeight = Math.min(maxSize, maxSize * 0.8);
    visDepth = Math.min(70, (parseFloat(formData.depth) / diameter) * maxSize * 0.5);
  } 
  else if (formData.poolType === 'oval') {
    const length = parseFloat(formData.length);
    const width = parseFloat(formData.width);
    const ratio = length / width;
    
    if (ratio > 1) {
      visWidth = maxSize;
      visHeight = maxSize / ratio;
    } else {
      visHeight = maxSize;
      visWidth = maxSize * ratio;
    }
    
    visDepth = Math.min(70, (parseFloat(formData.depth) / length) * maxSize * 0.8);
  }
  else {
    // Для других типов используем стандартные значения
    visWidth = visHeight = maxSize * 0.8;
    visDepth = 60;
  }

  // Цвета для визуализации
  const waterColors = {
    chlorine: '#4EAFF0',
    salt: '#79C4F5',
    bromine: '#5DA5E8',
    ozone: '#82CAF8',
    uv: '#5FB6F0'
  };
  
  const waterColor = waterColors[formData.waterType] || waterColors.chlorine;
  
  // Уровень воды (процент заполнения)
  const waterLevel = parseInt(formData.waterLevel) / 100;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5 mb-8">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">
          Визуализация объема бассейна
        </h3>
        
        {/* Вкладки для разных типов визуализации */}
        <div className="flex space-x-2 rounded-lg p-1 bg-gray-100 dark:bg-gray-700 shadow-inner">
          <button
            type="button"
            onClick={() => setActiveTab('3d')}
            className={`py-1 px-3 text-sm font-medium rounded-md ${
              activeTab === '3d'
                ? 'bg-white dark:bg-gray-600 shadow text-blue-600 dark:text-blue-400'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
            }`}
            aria-pressed={activeTab === '3d'}
          >
            <Box className="inline-block w-4 h-4 mr-1" />
            3D
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('comparison')}
            className={`py-1 px-3 text-sm font-medium rounded-md ${
              activeTab === 'comparison'
                ? 'bg-white dark:bg-gray-600 shadow text-blue-600 dark:text-blue-400'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
            }`}
            aria-pressed={activeTab === 'comparison'}
          >
            <Menu className="inline-block w-4 h-4 mr-1" />
            Сравнение
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('visual')}
            className={`py-1 px-3 text-sm font-medium rounded-md ${
              activeTab === 'visual'
                ? 'bg-white dark:bg-gray-600 shadow text-blue-600 dark:text-blue-400'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
            }`}
            aria-pressed={activeTab === 'visual'}
          >
            <LayoutList className="inline-block w-4 h-4 mr-1" />
            Визуальное сравнение
          </button>
        </div>
      </div>
      
      {/* 3D визуализация бассейна */}
      {activeTab === '3d' && (
        <div className="flex flex-col items-center justify-center py-4">
          <div 
            className="relative" 
            style={{
              width: `${visWidth + 60}px`,
              height: `${visHeight + visDepth + 40}px`,
              perspective: '800px'
            }}
          >
            {/* Имитация 3D бассейна */}
            <div 
              className="absolute top-0 left-0 border-2 border-gray-400 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 rounded-md transform-gpu"
              style={{
                width: `${visWidth}px`,
                height: `${visHeight}px`,
                transform: `rotateX(60deg) rotateZ(-5deg) translateZ(20px)`,
                transformStyle: 'preserve-3d'
              }}
            >
              {/* Дно бассейна */}
              <div 
                className="absolute left-0 top-0 right-0 bottom-0 bg-blue-50 dark:bg-blue-900/20 rounded-sm"
                style={{
                  transform: formData.bottomSlope === 'flat' ? '' : 
                            formData.bottomSlope === 'gradual' ? 'perspective(500px) rotateX(5deg)' : 
                            'perspective(500px) rotateX(10deg)'
                }}
              ></div>
              
              {/* Вода */}
              <div 
                className="absolute left-0 right-0 bottom-0 rounded-sm water-ripple"
                style={{
                  height: `${visHeight * waterLevel}px`,
                  backgroundColor: waterColor,
                  opacity: 0.7
                }}
              ></div>
            </div>
            
            {/* Боковая стенка (имитация глубины) */}
            <div 
              className="absolute border-r-2 border-b-2 border-gray-400 dark:border-gray-600 bg-gray-200 dark:bg-gray-700 rounded-br-md"
              style={{
                width: '20px',
                height: `${visDepth}px`,
                top: `${visHeight}px`,
                left: `${visWidth}px`,
                transform: 'rotateY(-10deg) skewY(30deg)'
              }}
            ></div>
            
            {/* Передняя стенка (имитация глубины) */}
            <div 
              className="absolute border-l-2 border-b-2 border-gray-400 dark:border-gray-600 bg-gray-200 dark:bg-gray-700 rounded-bl-md"
              style={{
                width: `${visWidth}px`,
                height: `${visDepth}px`,
                top: `${visHeight}px`,
                left: '0px',
                transform: 'rotateX(10deg) skewX(5deg)'
              }}
            ></div>
            
            {/* Подписи размеров */}
            <div 
              className="absolute text-xs text-gray-600 dark:text-gray-400"
              style={{
                top: `${visHeight + visDepth + 5}px`,
                left: `${visWidth / 2 - 20}px`
              }}
            >
              {formData.poolType === 'circular' 
                ? `Ø ${formData.diameter} ${formData.unitSystem === 'metric' ? 'м' : 'фт'}` 
                : `${formData.length}${formData.unitSystem === 'metric' ? 'м' : 'фт'}`
              }
            </div>
            
            <div 
              className="absolute text-xs text-gray-600 dark:text-gray-400 transform -rotate-90"
              style={{
                top: `${visHeight / 2 + 5}px`,
                left: `${-30}px`
              }}
            >
              {formData.poolType !== 'circular' 
                ? `${formData.width}${formData.unitSystem === 'metric' ? 'м' : 'фт'}` 
                : ''
              }
            </div>
            
            <div 
              className="absolute text-xs text-gray-600 dark:text-gray-400 transform -rotate-90"
              style={{
                top: `${visHeight + visDepth/2}px`,
                left: `${visWidth + 5}px`
              }}
            >
              {`${formData.depth}${formData.unitSystem === 'metric' ? 'м' : 'фт'}`}
            </div>
          </div>
          
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
              Объем воды: <span className="font-medium text-blue-600 dark:text-blue-400">{formatNumber(results.netVolume, 2)} м³</span>
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Заполнение: <span className="font-medium text-blue-600 dark:text-blue-400">{formData.waterLevel}%</span>
            </p>
          </div>
        </div>
      )}
      
      {/* Сравнение с известными объектами */}
      {activeTab === 'comparison' && (
        <div className="py-4">
          <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3">
            Ваш бассейн по объему сопоставим с:
          </h4>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Бутылки воды */}
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800 flex items-center">
              <div className="w-16 h-16 flex-shrink-0 text-blue-500 dark:text-blue-400 flex items-center justify-center">
                <span className="text-3xl">💧</span>
              </div>
              <div className="ml-3">
                <h5 className="text-sm font-medium text-gray-900 dark:text-white">Бутылки воды 1.5л</h5>
                <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {formatNumber(results.volumeLiters / 1.5, 0)}
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400">стандартных бутылок</p>
              </div>
            </div>
            
            {/* Ванны */}
            <div className="p-4 bg-cyan-50 dark:bg-cyan-900/20 rounded-lg border border-cyan-100 dark:border-cyan-800 flex items-center">
              <div className="w-16 h-16 flex-shrink-0 text-cyan-500 dark:text-cyan-400 flex items-center justify-center">
                <span className="text-3xl">🛁</span>
              </div>
              <div className="ml-3">
                <h5 className="text-sm font-medium text-gray-900 dark:text-white">Стандартные ванны</h5>
                <p className="text-2xl font-bold text-cyan-600 dark:text-cyan-400">
                  {formatNumber(results.netVolume / 0.2, 0)}
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400">ванн объемом 200л</p>
              </div>
            </div>
            
            {/* Душевые */}
            <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-100 dark:border-purple-800 flex items-center">
              <div className="w-16 h-16 flex-shrink-0 text-purple-500 dark:text-purple-400 flex items-center justify-center">
                <span className="text-3xl">🚿</span>
              </div>
              <div className="ml-3">
                <h5 className="text-sm font-medium text-gray-900 dark:text-white">Принятие душа</h5>
                <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                  {formatNumber(results.volumeLiters / 50, 0)}
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400">душей по 5 минут</p>
              </div>
            </div>
            
            {/* Цистерны */}
            <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-100 dark:border-emerald-800 flex items-center">
              <div className="w-16 h-16 flex-shrink-0 text-emerald-500 dark:text-emerald-400 flex items-center justify-center">
                <span className="text-3xl">🚒</span>
              </div>
              <div className="ml-3">
                <h5 className="text-sm font-medium text-gray-900 dark:text-white">Пожарные машины</h5>
                <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                  {formatNumber(results.netVolume / 2.5, 1)}
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400">цистерн по 2500л</p>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Визуальное сравнение */}
      {activeTab === 'visual' && (
        <div className="py-4">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Стандартные объемы бассейнов в сравнении с вашим ({formatNumber(results.netVolume, 1)} м³):
          </p>
          
          <div className="relative h-16 bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden mb-6">
            {/* Шкала */}
            <div className="absolute inset-0 flex">
              {[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map(percent => (
                <div key={percent} className="flex-1 border-l border-gray-300 dark:border-gray-600 relative">
                  <span className="absolute -bottom-6 left-0 text-xs text-gray-500 dark:text-gray-400 transform -translate-x-1/2">
                    {percent}м³
                  </span>
                </div>
              ))}
            </div>
            
            {/* Ваш бассейн */}
            <div 
              className="absolute top-0 h-full bg-blue-500 dark:bg-blue-600 opacity-70 rounded-r-sm flex items-center justify-end px-2"
              style={{ 
                width: `${Math.min(100, (results.netVolume / 100) * 100)}%`,
                minWidth: '20px'
              }}
            >
              <span className="text-xs font-medium text-white truncate">
                {Math.min(results.netVolume, 100) > 5 ? 'Ваш бассейн' : ''}
              </span>
            </div>
            
            {/* Другие типы бассейнов для сравнения */}
            <div className="absolute bottom-0 w-full flex text-xs">
              {/* Детский бассейн */}
              <div 
                className="absolute h-4 bg-green-400 dark:bg-green-500 opacity-70 rounded-sm"
                style={{ width: '3%', bottom: '20px' }}
              ></div>
              <span className="absolute text-xs text-gray-500 dark:text-gray-400" style={{ left: '3%', bottom: '28px', transform: 'translateX(-50%)' }}>
                Детский (3м³)
              </span>
              
              {/* Домашний средний */}
              <div 
                className="absolute h-4 bg-yellow-400 dark:bg-yellow-500 opacity-70 rounded-sm"
                style={{ width: '15%', bottom: '20px' }}
              ></div>
              <span className="absolute text-xs text-gray-500 dark:text-gray-400" style={{ left: '15%', bottom: '28px', transform: 'translateX(-50%)' }}>
                Обычный (15м³)
              </span>
              
              {/* Большой домашний */}
              <div 
                className="absolute h-4 bg-orange-400 dark:bg-orange-500 opacity-70 rounded-sm"
                style={{ width: '30%', bottom: '20px' }}
              ></div>
              <span className="absolute text-xs text-gray-500 dark:text-gray-400" style={{ left: '30%', bottom: '28px', transform: 'translateX(-50%)' }}>
                Большой (30м³)
              </span>
              
              {/* Общественный */}
              <div 
                className="absolute h-4 bg-red-400 dark:bg-red-500 opacity-70 rounded-sm"
                style={{ width: '60%', bottom: '20px' }}
              ></div>
              <span className="absolute text-xs text-gray-500 dark:text-gray-400" style={{ left: '60%', bottom: '28px', transform: 'translateX(-50%)' }}>
                Общественный (60м³)
              </span>
              
              {/* Олимпийский */}
              <div 
                className="absolute h-4 bg-purple-400 dark:bg-purple-500 opacity-70 rounded-sm"
                style={{ width: '100%', bottom: '20px' }}
              ></div>
              <span className="absolute text-xs text-gray-500 dark:text-gray-400" style={{ left: '100%', bottom: '28px', transform: 'translateX(-50%)' }}>
                Спортивный (меньше 100м³)
              </span>
            </div>
          </div>
          
          {/* Таблица сравнения с типичными бассейнами */}
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 mt-8">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Тип бассейна
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Объем (м³)
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Типичные размеры
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Сравнение с вашим
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-4 py-2">Детский надувной</td>
                  <td className="px-4 py-2">1-3</td>
                  <td className="px-4 py-2">Ø 2.5м × 0.6м</td>
                  <td className="px-4 py-2">
                    {results.netVolume > 3 ? 
                      <span className="text-green-600 dark:text-green-400">В {(results.netVolume / 3).toFixed(1)} раз больше</span> : 
                      <span className="text-red-600 dark:text-red-400">В {(3 / results.netVolume).toFixed(1)} раз меньше</span>
                    }
                  </td>
                </tr>
                <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-4 py-2">Каркасный дачный</td>
                  <td className="px-4 py-2">5-10</td>
                  <td className="px-4 py-2">3.5м × 2м × 1м</td>
                  <td className="px-4 py-2">
                    {results.netVolume > 7 ? 
                      <span className="text-green-600 dark:text-green-400">В {(results.netVolume / 7).toFixed(1)} раз больше</span> : 
                      <span className="text-red-600 dark:text-red-400">В {(7 / results.netVolume).toFixed(1)} раз меньше</span>
                    }
                  </td>
                </tr>
                <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-4 py-2">Частный стационарный</td>
                  <td className="px-4 py-2">15-25</td>
                  <td className="px-4 py-2">6м × 3м × 1.5м</td>
                  <td className="px-4 py-2">
                    {results.netVolume > 20 ? 
                      <span className="text-green-600 dark:text-green-400">В {(results.netVolume / 20).toFixed(1)} раз больше</span> : 
                      <span className="text-red-600 dark:text-red-400">В {(20 / results.netVolume).toFixed(1)} раз меньше</span>
                    }
                  </td>
                </tr>
                <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-4 py-2">Большой частный</td>
                  <td className="px-4 py-2">30-50</td>
                  <td className="px-4 py-2">8м × 4м × 1.8м</td>
                  <td className="px-4 py-2">
                    {results.netVolume > 40 ? 
                      <span className="text-green-600 dark:text-green-400">В {(results.netVolume / 40).toFixed(1)} раз больше</span> : 
                      <span className="text-red-600 dark:text-red-400">В {(40 / results.netVolume).toFixed(1)} раз меньше</span>
                    }
                  </td>
                </tr>
                <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-4 py-2">Мини-отель</td>
                  <td className="px-4 py-2">60-100</td>
                  <td className="px-4 py-2">10м × 6м × 1.8м</td>
                  <td className="px-4 py-2">
                    {results.netVolume > 80 ? 
                      <span className="text-green-600 dark:text-green-400">В {(results.netVolume / 80).toFixed(1)} раз больше</span> : 
                      <span className="text-red-600 dark:text-red-400">В {(80 / results.netVolume).toFixed(1)} раз меньше</span>
                    }
                  </td>
                </tr>
                <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-4 py-2">Спортивный 25м</td>
                  <td className="px-4 py-2">300-400</td>
                  <td className="px-4 py-2">25м × 12.5м × 1.8м</td>
                  <td className="px-4 py-2">
                    {results.netVolume > 350 ? 
                      <span className="text-green-600 dark:text-green-400">В {(results.netVolume / 350).toFixed(1)} раз больше</span> : 
                      <span className="text-red-600 dark:text-red-400">В {(350 / results.netVolume).toFixed(1)} раз меньше</span>
                    }
                  </td>
                </tr>
                <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-4 py-2">Олимпийский</td>
                  <td className="px-4 py-2">2000-2500</td>
                  <td className="px-4 py-2">50м × 25м × 2м</td>
                  <td className="px-4 py-2">
                    {results.netVolume > 2500 ? 
                      <span className="text-green-600 dark:text-green-400">В {(results.netVolume / 2500).toFixed(1)} раз больше</span> : 
                      <span className="text-red-600 dark:text-red-400">В {(2500 / results.netVolume).toFixed(1)} раз меньше</span>
                    }
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
            * Все сравнения приблизительны и основаны на средних значениях для типичных бассейнов в России.
          </p>
        </div>
      )}
    </div>
  );
};

export default VisualizationSection;